import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, Drawer, type Theme, Typography, useMediaQuery } from '@mui/material';
import ApprovalTabs from './ApprovalTabs';
import { type Approval, ApprovalDashboardProps, ApprovalDetails as ApprovalDetailsType, type ApprovalObject, IApprovalTab } from '../types';
import { ReactComponent as FileSvg } from '../images/files.svg';
import '../styles/ApprovalDashboard.scss';
import { useDispatch, useSelector } from 'react-redux';
import { setCommonAlerts } from 'modules/cbre-flow-Common/actions';
import { ApprovalContext } from './approvalContext';
import { tabs } from './utils';
import { ApprovalDetailsContainer } from 'modules/cbre-approval-details-widget';
import ApprovalList from 'modules/cbre-approvals-list/components/ApprovalList';
import useApprovalListener from './useApprovalListener';
import { ViewManagementButton, getViewsInfo, switchPortfolioToSystemDefaultView } from 'modules/cbre-flow-view-management';
import { getCurrentView } from 'modules/cbre-flow-view-management/ducks/selectors';
import WarningIcon from '@mui/icons-material/Warning';

const ApprovalDashboard: React.FC<ApprovalDashboardProps> = ({ components = {}, config }) => {

  // Get initial tab from URL or default to tabs[0]
  const getInitialTab = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const tabParam = urlParams.get('tab');
    if (tabParam) {
      const foundTab = tabs.find(tab => tab.value === tabParam);
      if (foundTab) return foundTab;
    }
    return tabs[0];
  };

  const [tabValue, setTabValue] = useState<IApprovalTab["tab"]>(getInitialTab());
  const [selectedApproval, setSelectedApproval] = useState<ApprovalObject | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const render = useRef({ isInitial: true }).current;
  const [page, setPage] = useState(0);
  const dispatch = useDispatch();
  const currentView = useSelector(getCurrentView);
  const viewInfo = useSelector(getViewsInfo);
  const { approvalsCount, approvalsList, approvalFilter } = config;
  const { storedValue: approval_decision_changes } = useApprovalListener("approval_decision_changes")
  const previousApprovalDescision = useRef({approval_decision_changes}).current;
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'));
  const isLaptop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const urlApprovalIdRef = useRef<string | null>(null);

  // Get approval ID from URL query params on initial load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const approvalId = urlParams.get('approvalId');
    if (approvalId) {
      urlApprovalIdRef.current = approvalId;
    }
  }, []);

  // Remove approval ID from URL
  const removeApprovalIdFromUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.delete('approvalId');
    const newUrl = urlParams.toString() 
      ? `${window.location.pathname}?${urlParams.toString()}`
      : window.location.pathname;
    window.history.replaceState({}, '', newUrl);
    urlApprovalIdRef.current = null;
  };

  // Calculate outside view count for the inline alert
  const outsideViewCount = (approvalsCount.outsideViewMyApprovalCount || 0) + (approvalsCount.outsideViewExternalApprovalCount || 0);

  const handleShowAllApprovals = () => {
    dispatch(switchPortfolioToSystemDefaultView(viewInfo?.clientCode));
  };

  useEffect(() => {
    // if(render.isInitial){
    //   render.isInitial = false;
    //   return;
    // }

    if(approvalFilter.filterPayload === null) return;

    const order = tabValue.value === "myApprovals" ? "asc" : "desc";
    const approvalId = urlApprovalIdRef.current;

    approvalsList.setQueryParams((prev: any) => ({ 
      ...prev, 
      search: '', 
      pageNumber: 1, 
      order, 
      filters: approvalFilter.filterPayload,
      ...(approvalId && { approvalId })
    }));
    approvalsList.setApprovalMethod(tabValue.value)
    approvalsList.handlePendingChanges().then((nextConfig) => {
      nextConfig.fetchApprovals(tabValue.value);
    });
    approvalsCount.fetchApprovalsCount();
    setSelectedApproval(null);
    setDetailsOpen(false);
  }, [tabValue, page, approval_decision_changes, currentView, JSON.stringify(approvalFilter.filterPayload)]);

  const handleTabChange = (newValue: IApprovalTab["tab"]) => {
    setTabValue(newValue);
    setPage(0);
  };

  const handleApprovalClick = async (approval: ApprovalObject) => {
    // const details = await fetchApprovalDetails(approval.id);
    setSelectedApproval(approval);
    setDetailsOpen(true);
  };

  const handleLoadMore = (count: number) => {
    const { params: { pageSize, pageNumber }, totalPageCount } = config.approvalsList
    const effectivePageSize = pageSize || 1;
    const newPageNumber = Math.ceil(count / effectivePageSize);
    if (pageNumber == newPageNumber + 1) return;
    if (newPageNumber + 1 > totalPageCount) return;
    config.approvalsList.setQueryParams((prev) => {
      return { ...prev, pageNumber: Number(newPageNumber || 0) + 1 }
    }
    );
    config.approvalsList.handlePendingChanges().then((nextConfig) => {
      nextConfig.fetchApprovals(tabValue.value, true);
    });
    // setPage((prevPage) => prevPage + 1);
  };

  const contextProvider = { config, tabValue }

  const handleClose = () => {
    setDetailsOpen(false);
    setSelectedApproval(null);
  }

  const { approvals } = config.approvalsList;   

  useEffect(() => {
      // If approvals list is empty, clear the selected approval
      if (approvals.length === 0) {
        setSelectedApproval(null);
        setDetailsOpen(false);
        return;
      }

      if(approval_decision_changes !== previousApprovalDescision.approval_decision_changes && approvals.length > 0){
          previousApprovalDescision.approval_decision_changes = approval_decision_changes;
          handleApprovalClick(approvals[0]);
      }
      
      // Check if URL approval ID exists in the response
      if (urlApprovalIdRef.current && approvals.length > 0) {
        const foundApproval = approvals.find(
          (approval: ApprovalObject) => approval.approvalFlow?.sourceMetaData?.approvalId === urlApprovalIdRef.current 
        );
        
        if (foundApproval) {
          handleApprovalClick(foundApproval);
          removeApprovalIdFromUrl();
        } else {
          dispatch(setCommonAlerts({ 
            message: `This approval no longer requires your review. You will find it on "My Approval History" or "External Approval History"`, 
            type: "failure" 
          }));
          removeApprovalIdFromUrl();
        }
      } else if(approvals.length > 0 && !selectedApproval){
        handleApprovalClick(approvals[0]);
      } else if (approvals.length > 0 && selectedApproval) {
        // Check if the currently selected approval exists in the new approvals list
        const approvalExists = approvals.some(
          (approval: ApprovalObject) => approval.approvalTaskId === selectedApproval.approvalTaskId
        );
        // If selected approval is not in the new list (e.g., after tab switch), select the first one
        if (!approvalExists) {
          handleApprovalClick(approvals[0]);
        }
      }
      }, [approvals, selectedApproval]);

  return (
    <ApprovalContext.Provider value={contextProvider}>
      <Box className="approval-dashboard">
        <header className="dashboard-header">
          {isDesktop && (
            <Box className="dashboard-title-row">
              <Box className="dashboard-title-left">
                <Typography variant="h4">Approvals</Typography>
                <ViewManagementButton />
              </Box>
              {outsideViewCount > 0 && viewInfo?.viewType === "custom" && (
                <Box className="outside-view-alert">
                  <WarningIcon className="alert-icon" />
                  <Typography className="alert-message">
                    {outsideViewCount} approval{outsideViewCount > 1 ? 's are' : ' is'} outside your current view.
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    className="alert-button"
                    onClick={handleShowAllApprovals}
                  >
                    Show all approvals
                  </Button>
                </Box>
              )}
            </Box>
          )}
          <ApprovalTabs value={tabValue.value} onChange={handleTabChange} tabs={tabs} />
        </header>
        <main className="dashboard-content">
          <ApprovalList
            onApprovalClick={handleApprovalClick}
            onLoadMore={handleLoadMore}
            selectedApproval={selectedApproval}
            components={components}
            config={config}
            tabValue={tabValue}
          />

          <Box className="approval-details-section">
            {selectedApproval ? isLaptop ? <ApprovalDetailsContainer approval={selectedApproval} key={selectedApproval.approvalTaskId} onClose={handleClose} />  : <Drawer anchor="right" open={detailsOpen} onClose={handleClose} sx={{ width: "100%" }} PaperProps={{
              sx: {
                width: '100%',
              },
            }}> <ApprovalDetailsContainer approval={selectedApproval} key={selectedApproval.approvalTaskId} onClose={handleClose} /> </Drawer> : (
              <div className={`no-approvals d-flex justify-content-center align-items-center flex-column h-100`} >
                <FileSvg />
              </div>
            )}

          </Box>

        </main>
      </Box>
    </ApprovalContext.Provider>

  );
};

export default ApprovalDashboard;